import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { fetchPaymentMethods } from '@/services/paymentMethods.service'
import i18n from '@/config/i18n'
import { DEFAULT_LOCALE } from '@/utils/constants'
import type { PaymentMethodOption } from '@/types/paymentMethods.types'

/**
 * Shipped defaults keep every payment form usable before the tenant config
 * loads (or when it fails). The API returns the same five codes.
 */
const FALLBACK_METHODS: PaymentMethodOption[] = [
  { code: 'cash', label_en: 'Cash', label_ar: 'نقداً' },
  { code: 'bank_transfer', label_en: 'Bank Transfer', label_ar: 'تحويل بنكي' },
  { code: 'check', label_en: 'Check', label_ar: 'شيك' },
  { code: 'credit_card', label_en: 'Credit Card', label_ar: 'بطاقة ائتمان' },
  { code: 'other', label_en: 'Other', label_ar: 'أخرى' },
]

const FALLBACK_BY_CODE = new Map(FALLBACK_METHODS.map((method) => [method.code, method]))

export interface PaymentMethodSelectOption {
  value: string
  label: string
}

export const usePaymentMethodsStore = defineStore('paymentMethods', () => {
  const methods = ref<PaymentMethodOption[]>([...FALLBACK_METHODS])
  const loaded = ref(false)
  let inflight: Promise<void> | null = null

  // Read the global i18n locale directly: importing the locale store here would pull the
  // router into every consumer (and break component tests that mock vue-router).
  const currentLocale = computed(
    () => (i18n.global as { locale?: { value?: string } }).locale?.value ?? DEFAULT_LOCALE,
  )
  const isArabic = computed(() => currentLocale.value.startsWith('ar'))

  /** Enabled methods as `{ value, label }` for select inputs, in tenant order. */
  const options = computed<PaymentMethodSelectOption[]>(() =>
    methods.value.map((method) => ({
      value: method.code,
      label: isArabic.value ? method.label_ar : method.label_en,
    })),
  )

  /** First enabled method — the default for new payments. */
  const defaultCode = computed(() => methods.value[0]?.code ?? 'bank_transfer')

  /**
   * Display label for any stored code. Historical payments may hold a code the
   * tenant no longer enables, so unknown codes fall back to a humanised label.
   */
  function labelFor(code?: string | null): string {
    if (!code) return ''
    const normalized = code.trim().toLowerCase()
    const known = methods.value.find((method) => method.code === normalized)
    if (known) return isArabic.value ? known.label_ar : known.label_en
    const fallback = FALLBACK_BY_CODE.get(normalized)
    if (fallback) return isArabic.value ? fallback.label_ar : fallback.label_en
    return normalized
      .replace(/[_-]+/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase())
  }

  async function load(force = false): Promise<void> {
    if (inflight) return inflight
    if (loaded.value && !force) return

    inflight = (async () => {
      const payload = await fetchPaymentMethods()
      if (payload?.length) {
        methods.value = payload
      }
      loaded.value = true
    })()

    try {
      await inflight
    } finally {
      inflight = null
    }
  }

  /** Replace the cached list (after the settings page saves). */
  function setMethods(next: PaymentMethodOption[]): void {
    methods.value = next.length ? next : [...FALLBACK_METHODS]
    loaded.value = true
  }

  /** Restore shipped defaults on logout / tenant switch. */
  function reset(): void {
    methods.value = [...FALLBACK_METHODS]
    loaded.value = false
  }

  return {
    methods,
    loaded,
    options,
    defaultCode,
    labelFor,
    load,
    setMethods,
    reset,
  }
})
